import { DiscussionContext, DiscussionType } from "@cnx/common";
import { Box, Menu, Text } from "@mantine/core";
import {
  IconChevronLeft,
  IconDotsVertical,
  IconPhone,
  IconX,
} from "@tabler/icons-react";
import clsx from "clsx";
import UserAvatar from "components/UserAvatar";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import ChatTitle from "./ChatTitle";
import DiscussionIcon from "./DiscussionIcon";

interface ChatHeaderBarProps {
  discussionType?: DiscussionType;
  context?: string | null;
  stopActivityKind?: string | null;
  participantName?: string;
  avatarId?: string | null;
  title?: string;
  discussionContext?: DiscussionContext;
  typingLabel?: string | null;
  phone?: string | null;
  showActions?: boolean;
  onGoBack: () => void;
  onClose?: () => void;
  onViewParticipants?: () => void;
  className?: string;
}

export const ChatHeaderBar = ({
  discussionType,
  context,
  stopActivityKind,
  participantName,
  avatarId,
  title,
  discussionContext,
  typingLabel,
  phone,
  showActions = false,
  onGoBack,
  onClose,
  onViewParticipants,
  className,
}: ChatHeaderBarProps) => {
  const { t } = useTranslation("discussions");

  return (
    <Box
      className={clsx(
        className,
        "bg-white w-full flex items-start justify-between sticky top-0 z-10"
      )}
      mih={66}
    >
      <Box className="flex items-center gap-2" mih={66}>
        <button
          className="m-2 cursor-pointer"
          aria-label="Back"
          onClick={onGoBack}
        >
          <IconChevronLeft
            height={24}
            width={24}
            color="var(--color-gray-dark)"
          />
        </button>

        {discussionType === DiscussionType.CONTEXT ? (
          <DiscussionIcon
            discussionType={discussionType}
            context={context}
            stopActivityKind={stopActivityKind}
            size={40}
          />
        ) : (
          <UserAvatar userName={participantName} avatarId={avatarId ?? ""} />
        )}
      </Box>
      <Box className="flex flex-col items-start px-4 grow" mih={66} pt={16}>
        <ChatTitle title={title} discussionContext={discussionContext} />
        {typingLabel && (
          <Text fz={"sm"} c={"var(--color-blue)"}>
            {typingLabel} {t("typing").toLowerCase()}
          </Text>
        )}
      </Box>
      <Box className="flex items-center gap-2 mr-2" mih={66}>
        {phone && (
          <Link className="p-2" to={`tel:${phone}`}>
            <IconPhone className="text-green" />
          </Link>
        )}
        {showActions && (
          <>
            <Menu shadow="md" width={200} withArrow arrowOffset={0} offset={15}>
              <Menu.Target>
                <button
                  className="p-2 cursor-pointer"
                  aria-label="More options"
                >
                  <IconDotsVertical height={24} width={24} />
                </button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={onViewParticipants}>
                  {discussionType === DiscussionType.PRIVATE
                    ? t("viewContact")
                    : t("viewGroupMembers")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-0 flex items-center text-gray-dark hover:text-black cursor-pointer"
            >
              <IconX size={32} />
            </button>
          </>
        )}
      </Box>
    </Box>
  );
};
